import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import { useWebsiteContent } from '../hooks/useWebsiteContent';
import image1 from '../assets/homepage-images/boxes-outside-van.jpg';
import image2 from '../assets/homepage-images/warehouse.jpg';
import image3 from '../assets/homepage-images/lorries-hub.jpg';
import image4 from '../assets/homepage-images/whiskey-drinks.jpg';
import amazonLogo from '../assets/partner-logos/amazon.svg';
import tescoMarketplaceLogo from '../assets/partner-logos/tesco-marketplace.svg';
import waldosEmporiumLogo from '../assets/partner-logos/waldos-emporium.webp';
import './Home.css';

const Home = () => {
  const { content, loading } = useWebsiteContent('homepage');

  if (loading) {
    return <div style={{ padding: '2rem', textAlign: 'center' }}>Loading...</div>;
  }

  return (
    <div className="home">
      <SEO
        title="MarketVendr - UK Import & Distribution Services"
        description="Import products to the UK legally and safely. MarketVendr provides UK distribution, customs clearance, and warehousing services for international businesses, including alcohol imports. Expert compliance and logistics from our UK warehouse in Grimsby."
        keywords="UK import services, import to UK, UK distribution services, alcohol import UK, UK customs clearance, import products to UK, UK warehousing, UK logistics, international shipping UK, UK market entry, UK import compliance, UK distribution warehouse, import alcohol to UK legally, UK import distribution, UK import logistics, customs clearance UK, UK import warehouse, import goods to UK, UK distribution center, UK import services for businesses, import alcohol to UK, UK alcohol import services, UK import from abroad, UK distribution from warehouse, UK import compliance services"
        url="https://www.marketvendr.com/"
        structuredData={{
          "@context": "https://schema.org",
          "@type": "WebSite",
          "name": "MarketVendr",
          "url": "https://www.marketvendr.com",
          "potentialAction": {
            "@type": "SearchAction",
            "target": "https://www.marketvendr.com/search?q={search_term_string}",
            "query-input": "required name=search_term_string"
          },
          "mainEntity": {
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "How do I import products to the UK?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "MarketVendr provides comprehensive UK import services including customs clearance, warehousing, and distribution. We handle the entire import process from origin to our UK warehouse in Grimsby, ensuring legal compliance and smooth delivery to your customers."
                }
              },
              {
                "@type": "Question",
                "name": "Can I import alcohol to the UK?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes, MarketVendr specializes in legal alcohol imports to the UK. We handle all necessary licenses, customs clearance, and compliance requirements to ensure your alcohol products are imported safely and legally to our UK warehouse."
                }
              },
              {
                "@type": "Question",
                "name": "Where is your UK warehouse located?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Our UK warehouse is located at 53 Energy Parkway, Grimsby, DN31 2TT. This strategic location provides excellent access to UK distribution networks and ports for efficient import and distribution services."
                }
              },
              {
                "@type": "Question",
                "name": "What services do you provide for UK imports?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "MarketVendr provides end-to-end UK import services including customs clearance, warehousing, distribution, label compliance, and logistics management. We handle products from origin to final delivery to your UK customers."
                }
              },
              {
                "@type": "Question",
                "name": "How do I get started with UK import services?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Contact MarketVendr at Contact@marketvendr.com or call 01472 282216. Our team will discuss your import needs, provide a quote, and guide you through the process of importing products to the UK legally and safely."
                }
              }
            ]
          }
        }}
      />
      <section className="hero">
        <div className="hero-container">
          <div className="hero-content">
            <div className="hero-badge">{content.hero_badge || 'Your Partner in UK Distribution'}</div>
            <h1>{content.hero_title || 'Streamline Your Supply Chain'}</h1>
            <p className="hero-subtitle">
              {content.hero_subtitle || 'Simplifying and accelerating UK market entry for global brands through direct, reliable distribution. Streamlined access to D2C marketplaces and B2B wholesale channels with transparent operations.'}
            </p>
            <div className="hero-buttons">
              <Link to="/contact" className="btn btn-primary">
                Get Started
              </Link>
              <Link to="/vendrs" className="btn btn-secondary">
                View Vendors
              </Link>
            </div>
          </div>
          <div className="hero-visual">
            <div className="hero-grid">
              <div className="hero-block hero-block-1"></div>
              <div className="hero-block hero-block-2">
                <img src={image1} alt="Cargo delivery van for UK distribution services" />
              </div>
              <div className="hero-block hero-block-3"></div>
              <div className="hero-block hero-block-4">
                <img src={image2} alt="Modern UK warehouse interior for product storage and distribution" />
              </div>
              <div className="hero-block hero-block-5"></div>
              <div className="hero-block hero-block-6">
                <img src={image3} alt="Trucks in distribution hub for UK import and logistics services" />
              </div>
              <div className="hero-block hero-block-7"></div>
              <div className="hero-block hero-block-8">
                <img src={image4} alt="Alcohol products ready for UK import and distribution" />
              </div>
              <div className="hero-block hero-block-9"></div>
            </div>
          </div>
        </div>
      </section>

      <section className="partners" aria-labelledby="partners-title">
        <div className="container">
          <h2 id="partners-title" className="partners-title">
            {content.partners_title || 'Marketplace & Distribution Partners'}
          </h2>
          <div className="partners-logos">
            <div className="partner-logo">
              <img src={amazonLogo} alt="Amazon" loading="lazy" />
            </div>
            <div className="partner-logo">
              <img src={tescoMarketplaceLogo} alt="Tesco Marketplace" loading="lazy" />
            </div>
            <div className="partner-logo partner-logo-tall">
              <img src={waldosEmporiumLogo} alt="Mr. Waldo's Wonderful Drinking Emporium" loading="lazy" />
            </div>
          </div>
        </div>
      </section>

      <section className="services-overview">
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: '0.25rem' }}>
            <p className="section-subtitle">{content.services_subtitle || 'Tailored for You'}</p>
          </div>
          <h2 className="section-title">{content.services_title || 'Our Services'}</h2>
          <div className="services-block">
            <div className="services-grid-block">
              <div className="block-left">
                <h3>{content.we_provide_title || 'We Provide'}</h3>
              </div>
              <div className="block-right">
                <ul className="block-list">
                  <li>{content.we_provide_item1 || 'Streamlined direct access to UK market without intermediaries.'}</li>
                  <li>{content.we_provide_item2 || 'Specialised in UK distribution with a proven track record.'}</li>
                  <li>{content.we_provide_item3 || 'Market Reach enabling access to established D2C marketplaces and / or focus on B2B wholesale'}</li>
                  <li>{content.we_provide_item4 || 'Professional Support so you can focus on your UK Sales Strategy knowing that all other aspects are taken care of'}</li>
                </ul>
              </div>
            </div>

            <div className="services-grid-block">
              <div className="block-left">
                <h3>{content.logistics_title || 'End-to-End Logistics & Distribution'}</h3>
              </div>
              <div className="block-right">
                <ul className="block-list">
                  <li>{content.logistics_item1 || 'Efficient delivery from origin to our UK warehouse'}</li>
                  <li>{content.logistics_item2 || 'Safe and secure storage for your products in our UK warehouse.'}</li>
                  <li>{content.logistics_item3 || 'Retain title of your goods and enjoy clear visibility of your inventory 24/7.'}</li>
                  <li>{content.logistics_item4 || 'Fast and reliable delivery to your customers across the UK'}</li>
                </ul>
              </div>
            </div>

            <div className="services-grid-block">
              <div className="block-left">
                <h3>{content.customs_title || 'Customs Clearance for Hassle-Free Importation'}</h3>
              </div>
              <div className="block-right">
                <ul className="block-list">
                  <li>{content.customs_item1 || 'Effortless customs clearance, eliminating delays'}</li>
                  <li>{content.customs_item2 || 'Experienced Brokers providing expert navigation of UK customs regulations'}</li>
                  <li>{content.customs_item3 || 'Ensure smooth and compliant importation'}</li>
                </ul>
              </div>
            </div>

            <div className="services-grid-block">
              <div className="block-left">
                <h3>{content.labels_title || 'Label Compliance'}</h3>
              </div>
              <div className="block-right">
                <ul className="block-list">
                  <li>{content.labels_item1 || 'Labelling Expertise: We navigate complex UK labelling requirements to ensure your product is UK ready'}</li>
                  <li>{content.labels_item2 || 'Regulatory Compliance: Ensuring that your products meet all UK standards'}</li>
                  <li>{content.labels_item3 || 'Seamless Integration: Simplifying the process for efficient product launch'}</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="why-choose-us">
        <div className="container">
          <h2 className="section-title">{content.why_title || 'Why Choose Us'}</h2>
          <div className="features-grid">
            <div className="feature">
              <h3>{content.expert_title || 'Expert Team'}</h3>
              <p>{content.expert_desc || 'Our experienced professionals understand the nuances of international trade and logistics.'}</p>
            </div>
            <div className="feature">
              <h3>{content.network_title || 'Global Network'}</h3>
              <p>{content.network_desc || 'Extensive network of partners and facilities across key markets worldwide.'}</p>
            </div>
            <div className="feature">
              <h3>{content.tech_title || 'Technology-Driven'}</h3>
              <p>{content.tech_desc || 'Advanced systems and tools to track, manage, and optimize your supply chain.'}</p>
            </div>
            <div className="feature">
              <h3>{content.customer_title || 'Customer-Focused'}</h3>
              <p>{content.customer_desc || 'Dedicated support team committed to your success and satisfaction.'}</p>
            </div>
          </div>
        </div>
      </section>

      <section className="cta-section">
        <div className="container">
          <h2>{content.cta_title || 'Ready to Transform Your Supply Chain?'}</h2>
          <p>{content.cta_subtitle || 'Join other businesses already trusting MarketVendr for their logistics needs.'}</p>
          <Link to="/contact" className="btn btn-primary btn-large">
            {content.cta_button || 'Contact Us Today'}
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;

